import { useState } from "react";
import { Trash2, CheckCircle, XCircle, Clock, AlertTriangle, User, Calendar, MessageSquare } from "lucide-react";
import BatchActionBar from "../../components/BatchActionBar";

const demoRequests = [
    {
        id: 1,
        user: {
            name: "Priya Sharma",
            avatar: "https://i.pravatar.cc/100?img=1",
            email: "priya.sharma@email.com",
            memberSince: "January 2024"
        },
        reason: "Privacy Concerns",
        feedback: "I'm concerned about my data privacy and want all my information removed from the platform.",
        requestedOn: "February 2nd, 2026",
        status: "Pending"
    },
    {
        id: 2,
        user: {
            name: "Rahul Verma",
            avatar: "https://i.pravatar.cc/100?img=2",
            email: "rahul.verma@email.com",
            memberSince: "March 2023"
        },
        reason: "Found Partner",
        feedback: "I found my life partner through this app! Thank you for everything. Now I want to delete my account.",
        requestedOn: "February 1st, 2026",
        status: "Pending"
    },
    {
        id: 3,
        user: {
            name: "Anita Kapoor",
            avatar: "https://i.pravatar.cc/100?img=3",
            email: "anita.kapoor@email.com",
            memberSince: "June 2024"
        },
        reason: "Not Using Anymore",
        feedback: "I'm not actively using the app anymore and would like to delete my account.",
        requestedOn: "January 30th, 2026",
        status: "Approved"
    },
    {
        id: 4,
        user: {
            name: "Vikram Singh",
            avatar: "https://i.pravatar.cc/100?img=4",
            email: "vikram.singh@email.com",
            memberSince: "September 2023"
        },
        reason: "Bad Experience",
        feedback: "I had negative experiences with some users and don't want to continue using this platform.",
        requestedOn: "January 28th, 2026",
        status: "Pending"
    },
    {
        id: 5,
        user: {
            name: "Meera Patel",
            avatar: "https://i.pravatar.cc/100?img=5",
            email: "meera.patel@email.com",
            memberSince: "December 2024"
        },
        reason: "Other",
        feedback: "Personal reasons. Please process my deletion request as soon as possible.",
        requestedOn: "January 25th, 2026",
        status: "Rejected"
    }
];

export default function DeletionRequests() {
    const [filterStatus, setFilterStatus] = useState("all");
    const [requestsList, setRequestsList] = useState(demoRequests);
    const [selectedIds, setSelectedIds] = useState([]);

    const filteredRequests = requestsList.filter(request => {
        if (filterStatus === "all") return true;
        return request.status.toLowerCase() === filterStatus;
    });

    const handleToggleSelectAll = () => {
        if (selectedIds.length === filteredRequests.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredRequests.map((r) => r.id));
        }
    };

    const handleToggleSelectRow = (id) => {
        setSelectedIds((prev) =>
            prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
        );
    };

    const handleBatchDelete = () => {
        setRequestsList((prev) => prev.filter((r) => !selectedIds.includes(r.id)));
        setSelectedIds([]);
    };

    const pendingCount = requestsList.filter(r => r.status === "Pending").length;

    const getStatusColor = (status) => {
        switch (status) {
            case "Pending": return "bg-yellow-100 dark:bg-yellow-500/20 text-yellow-600 dark:text-yellow-400";
            case "Approved": return "bg-green-100 dark:bg-green-500/20 text-green-600 dark:text-green-400";
            case "Rejected": return "bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-gray-200";
            default: return "bg-gray-100 dark:bg-gray-500/20 text-gray-600";
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
                Account Deletion Requests
            </h1>

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-red-500 to-rose-500 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-80">Total Requests</p>
                            <p className="text-2xl font-bold">{requestsList.length}</p>
                        </div>
                        <Trash2 className="w-10 h-10 opacity-50" />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-yellow-500 to-amber-500 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-80">Pending</p>
                            <p className="text-2xl font-bold">{pendingCount}</p>
                        </div>
                        <Clock className="w-10 h-10 opacity-50" />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-80">Approved</p>
                            <p className="text-2xl font-bold">{requestsList.filter(r => r.status === "Approved").length}</p>
                        </div>
                        <CheckCircle className="w-10 h-10 opacity-50" />
                    </div>
                </div>
                <div className="bg-gradient-to-br from-gray-500 to-slate-500 rounded-xl p-4 text-white">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm opacity-80">Rejected</p>
                            <p className="text-2xl font-bold">{requestsList.filter(r => r.status === "Rejected").length}</p>
                        </div>
                        <XCircle className="w-10 h-10 opacity-50" />
                    </div>
                </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2">
                {[
                    { id: "all", label: "All Requests" },
                    { id: "pending", label: "Pending" },
                    { id: "approved", label: "Approved" },
                    { id: "rejected", label: "Rejected" }
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => {
                            setFilterStatus(tab.id);
                            setSelectedIds([]);
                        }}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filterStatus === tab.id
                                ? "bg-gradient-to-r from-pink-500 to-rose-500 text-white"
                                : "bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300"
                            }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            {/* Requests List */}
            <div className="space-y-4">
                {filteredRequests.map(request => {
                    const isSelected = selectedIds.includes(request.id);
                    return (
                    <div
                        key={request.id}
                        className={`bg-white dark:bg-slate-800 rounded-xl shadow-lg border p-6 transition-colors ${isSelected ? "border-pink-500 bg-pink-50/20 dark:bg-pink-500/10" : "border-gray-200 dark:border-slate-700"}`}
                    >
                        <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                            {/* Checkbox and User Info */}
                            <div className="flex items-start gap-4">
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => handleToggleSelectRow(request.id)}
                                    className="mt-4 w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-pink-600 focus:ring-pink-500 cursor-pointer"
                                />
                                <img
                                    src={request.user.avatar}
                                    alt={request.user.name}
                                    className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-200 dark:ring-slate-600"
                                />
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{request.user.name}</h3>
                                    <p className="text-sm text-gray-500">{request.user.email}</p>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                                        <span className="flex items-center gap-1">
                                            <User className="w-3 h-3" />
                                            Member since {request.user.memberSince}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            Requested: {request.requestedOn}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Status & Actions */}
                            <div className="flex items-center gap-3">
                                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(request.status)}`}>
                                    {request.status}
                                </span>
                                {request.status === "Pending" && (
                                    <div className="flex items-center gap-2">
                                        <button className="flex items-center gap-1 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600">
                                            <CheckCircle className="w-4 h-4" />
                                            Approve
                                        </button>
                                        <button className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-xs font-medium rounded-lg hover:bg-red-600">
                                            <XCircle className="w-4 h-4" />
                                            Reject
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Reason & Feedback */}
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                            <div className="flex items-center gap-2 mb-2">
                                <AlertTriangle className="w-4 h-4 text-orange-500" />
                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Reason: {request.reason}</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-gray-400 mt-0.5" />
                                <p className="text-sm text-gray-500 dark:text-gray-400">{request.feedback}</p>
                            </div>
                        </div>
                    </div>
                    );
                })}
            </div>

            {/* Floating Multi-Delete Bar */}
            <BatchActionBar
                selectedCount={selectedIds.length}
                totalCount={filteredRequests.length}
                onClearSelection={() => setSelectedIds([])}
                onSelectAll={handleToggleSelectAll}
                onDeleteSelected={handleBatchDelete}
                entityName="deletion requests"
            />
        </div>
    );
}
